import { useCallback, useEffect, useState, Fragment, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { s, MdmShell, Field, ErrorAlert } from './_mdmKit.js';
import { Button } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { RecordPicker } from '../../components/RecordPicker.js';

/** M05 · C5 — Master Version History Viewer.
 * Wires the frozen `/master/version-history` contract:
 *   GET `/`                 ?entityType&entityId → T068[]
 *   GET `/:versionId/snapshot` → snapshot object */

/** A master record as GET /master/approval-queue/entities lists it (G18: picked by code and name). */
interface MasterRecord { entityId: string; code: string; name: string }

interface Version {
  versionId: string;
  entityType: string;
  entityId: string;
  versionNumber: number;
  changeType: string;
  changeReason: string | null;
  changedBy: string;
  changedAt: string;
}

function errText(e: unknown): string {
  if (e && typeof e === 'object') {
    const body = (e as { body?: unknown }).body;
    if (body && typeof body === 'object' && 'message' in body) {
      const m = (body as { message?: unknown }).message;
      if (typeof m === 'string') return m;
      if (Array.isArray(m)) return m.join(', ');
    }
    const msg = (e as { message?: unknown }).message;
    if (typeof msg === 'string') return msg;
  }
  return 'Request failed. Please try again.';
}

export function VersionHistoryViewerPage() {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();

  const [entityTypes, setEntityTypes] = useState<string[]>([]);
  const [entityType, setEntityType] = useState<string>('');
  const [entityId, setEntityId] = useState<string>('');
  const [rows, setRows] = useState<Version[] | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [snapshots, setSnapshots] = useState<Record<string, Record<string, unknown>>>({});

  // The master types come from the server, which owns the list.
  useEffect(() => {
    let alive = true;
    apiRequest<{ data: string[] }>('/master/approval-queue/entity-types', { token })
      .then((r) => {
        if (!alive) return;
        setEntityTypes(r.data);
        setEntityType((cur) => cur || (r.data[0] ?? ''));
      })
      .catch((err) => { if (alive) setError(errText(err)); });
    return () => { alive = false; };
  }, [token]);

  const load = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError(null);
      if (!entityType || !entityId) {
        setError(t('mdm.version.bothReq'));
        return;
      }
      setRows(null);
      setLoaded(true);
      setExpanded(null);
      setSnapshots({});
      try {
        const res = await apiRequest<Version[]>(
          `/master/version-history?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}`,
          { token },
        );
        setRows(res);
      } catch (err) {
        setError(errText(err));
        setRows([]);
      }
    },
    [entityType, entityId, token],
  );

  const viewSnapshot = useCallback(
    async (versionId: string) => {
      if (expanded === versionId) {
        setExpanded(null);
        return;
      }
      setExpanded(versionId);
      if (snapshots[versionId]) return;
      try {
        const snap = await apiRequest<Record<string, unknown>>(
          `/master/version-history/${versionId}/snapshot`,
          { token },
        );
        setSnapshots((prev) => ({ ...prev, [versionId]: snap }));
      } catch (err) {
        setError(errText(err));
      }
    },
    [expanded, snapshots, token],
  );

  return (
    <MdmShell
      label="Master version history viewer"
      title={t('mdm.version.title')}
      description={t('mdm.version.description')}
      backTo="/admin/mdm/lookups"
      backLabel={t('mdm.version.backLabel')}
    >
      <form aria-label="Load version history" onSubmit={load} style={{ ...s.card, marginBottom: 20 }}>
        {error && <ErrorAlert message={error} />}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
          <Field id="vh-entity-type" label={t('mdm.version.entityType')}>
            <select id="vh-entity-type" style={s.input} value={entityType} onChange={(e) => { setEntityType(e.target.value); setEntityId(''); }}>
              {entityTypes.map((et) => (<option key={et} value={et}>{et}</option>))}
            </select>
          </Field>
          <RecordPicker<MasterRecord>
            endpoint={entityType ? `/master/approval-queue/entities?entityType=${encodeURIComponent(entityType)}` : ''}
            idField="entityId"
            optionLabel={(m) => `${m.code} — ${m.name}`}
            value={entityId}
            onPick={(id) => setEntityId(id)}
            label={t('mdm.version.entityId')}
            testId="vh-entity-id"
          />
        </div>
        <div style={{ marginTop: 16 }}>
          <Button type="submit" data-testid="load-history" variant="primary">{t('mdm.version.loadHistory')}</Button>
        </div>
      </form>

      {!loaded ? (
        <p data-testid="history-prompt" style={{ color: s.muted }}>{t('mdm.version.prompt')}</p>
      ) : rows === null ? (
        <p>{t('mdm.version.loadingHistory')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="history-empty" style={{ color: s.muted }}>{t('mdm.version.empty')}</p>
      ) : (
        <table aria-label="Version history" style={s.table} data-testid="history-table">
          <thead>
            <tr>
              <th style={s.th}>{t('mdm.version.col.version')}</th>
              <th style={s.th}>{t('mdm.version.col.changeType')}</th>
              <th style={s.th}>{t('mdm.version.col.reason')}</th>
              <th style={s.th}>{t('mdm.version.col.changedBy')}</th>
              <th style={s.th}>{t('mdm.version.col.changedAt')}</th>
              <th style={s.th}>{t('mdm.version.col.snapshot')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((v) => (
              <Fragment key={v.versionId}>
                <tr data-testid={`version-row-${v.versionId}`}>
                  <td style={s.td}>v{v.versionNumber}</td>
                  <td style={s.td}>{v.changeType}</td>
                  <td style={s.td}>{v.changeReason ?? '—'}</td>
                  <td style={{ ...s.td, fontFamily: 'monospace', fontSize: 12 }}>{v.changedBy}</td>
                  <td style={s.td}>{v.changedAt ? at(v.changedAt) : '—'}</td>
                  <td style={s.td}>
                    <Button type="button" data-testid={`view-snapshot-${v.versionId}`} variant="secondary" onClick={() => void viewSnapshot(v.versionId)}>
                      {expanded === v.versionId ? t('mdm.version.hideSnapshot') : t('mdm.version.viewSnapshot')}
                    </Button>
                  </td>
                </tr>
                {expanded === v.versionId && (
                  <tr data-testid={`snapshot-${v.versionId}`}>
                    <td style={s.td} colSpan={6}>
                      {snapshots[v.versionId] ? (
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>
                          {JSON.stringify(snapshots[v.versionId], null, 2)}
                        </pre>
                      ) : (
                        <p style={{ margin: 0 }}>{t('mdm.version.loadingSnapshot')}</p>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </MdmShell>
  );
}

export default VersionHistoryViewerPage;
